import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography } from "@mui/material";
import { Fields, hasOptionalFieldPermission } from "imports/Utils";
import { FormattedDate  } from "utils/DateUtils";
import { VoyageCallTimesBlockFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";

interface VoyageCallTimesDetailsProps {
	call: Pick<VoyageCallTimesBlockFragment,
		"pta" |
		"ptd" |
		"eta" |
		"etso" |
		"eteo" |
		"etd" |
		"ata" |
		"atso" |
		"ateo" |
		"atd" |
		"atn" |
		"dtn" |
		"simulatedTimestamp" |
		"systemAta" |
		"systemEta">
	size?: "normal" | "small",
}

const VoyageCallTimesDetails = ({
	call,
	size = "normal",
}: VoyageCallTimesDetailsProps) => {
	const session = useStoreState(store => store.session);
	const hasNotificationTimes = hasOptionalFieldPermission(session, Fields.CALL, "notificationTime");

	const fontSize = size === "normal" ? 13 : 11;

	const textSx = { fontSize: fontSize, fontWeight: 500 };
	const dateSx = { fontSize: fontSize, fontWeight: 400 };

	const renderSystemDateCell = (
		actualDate: number | undefined | null,
		systemDate: number | undefined | null,
	) => {
		const currentDate = actualDate ?? systemDate;
		const isSystemDate = !actualDate && !!systemDate;

		return (
			<Tooltip
				title={isSystemDate ? intl.formatMessage({ id: "CALCULATED_BY_SYSTEM" }) : undefined}
				arrow
				placement="top">
				<Typography
					color="text.primary"
					sx={{ fontStyle: isSystemDate ? "italic" : "inherit", ...dateSx }}>
					{currentDate ? <FormattedDate date={currentDate} withoutYear /> : "-"}
				</Typography>
			</Tooltip>
		);
	};

	return (
		<TableContainer>
			<Table
				size="small"
				sx={{
					"td": { padding: 0.35, fontSize: 12 },
					"& td, & th": { border: 0, verticalAlign: "top" },
				}}>
				<TableBody>
					<TableRow>
						<TableCell />

						<TableCell>
							<Typography color="text.primary" sx={textSx}>
								{intl.formatMessage({ id: "PLANNED" })}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color="text.primary" sx={textSx}>
								{intl.formatMessage({ id: "ESTIMATED" })}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color="text.primary" sx={textSx}>
								{intl.formatMessage({ id: "ACTUAL" })}
							</Typography>
						</TableCell>

						{hasNotificationTimes && (
							<TableCell>
								<Typography color="text.primary" sx={textSx}>
									{intl.formatMessage({ id: "NOTIFIED" })}
								</Typography>
							</TableCell>
						)}
					</TableRow>

					<TableRow>
						<TableCell>
							<Typography color="text.primary" sx={textSx}>
								{intl.formatMessage({ id: "ARRIVAL" })}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color={call.simulatedTimestamp ? "grey" : "text.primary"} sx={dateSx}>
								{call.pta ? <FormattedDate date={call.pta} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						<TableCell>
							{renderSystemDateCell(call.eta, call.systemEta)}
						</TableCell>

						<TableCell>
							{renderSystemDateCell(call.ata, call.systemAta)}
						</TableCell>

						{hasNotificationTimes && (
							<TableCell>
								<Typography color="text.primary" sx={dateSx}>
									{call.atn ? <FormattedDate date={call.atn} withoutYear /> : "-"}
								</Typography>
							</TableCell>
						)}
					</TableRow>

					<TableRow>
						<TableCell>
							<Typography color="text.primary" sx={textSx}>
								{intl.formatMessage({ id: "START_OPERATION" })}
							</Typography>
						</TableCell>

						<TableCell />

						<TableCell>
							<Typography color="text.primary" sx={dateSx}>
								{call.etso ? <FormattedDate date={call.etso} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color="text.primary" sx={dateSx}>
								{call.atso ? <FormattedDate date={call.atso} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						<TableCell />
					</TableRow>

					<TableRow>
						<TableCell>
							<Typography color="text.primary" sx={textSx}>
								{intl.formatMessage({ id: "END_OPERATION" })}
							</Typography>
						</TableCell>

						<TableCell />

						<TableCell>
							<Typography color="text.primary" sx={dateSx}>
								{call.eteo ? <FormattedDate date={call.eteo} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color="text.primary" sx={dateSx}>
								{call.ateo ? <FormattedDate date={call.ateo} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						<TableCell />
					</TableRow>

					<TableRow>
						<TableCell>
							<Typography color="text.primary" sx={textSx}>
								{intl.formatMessage({ id: "DEPARTURE" })}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color={call.simulatedTimestamp ? "grey" : "text.primary"} sx={dateSx}>
								{call.ptd ? <FormattedDate date={call.ptd} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color="text.primary" sx={dateSx}>
								{call.etd ? <FormattedDate date={call.etd} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						<TableCell>
							<Typography color="text.primary" sx={dateSx}>
								{call.atd ? <FormattedDate date={call.atd} withoutYear /> : "-"}
							</Typography>
						</TableCell>

						{hasNotificationTimes && (
							<TableCell>
								<Typography color="text.primary" sx={dateSx}>
									{call.dtn ? <FormattedDate date={call.dtn} withoutYear /> : "-"}
								</Typography>
							</TableCell>
						)}
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default VoyageCallTimesDetails;
